import type { ThemeColors } from "./theme.types"

export class LightTheme implements ThemeColors {
  readonly name = "light" as const

  readonly bgCanvas = "#F4F5F7"
  readonly bgSurface = "#FFFFFF"
  readonly textPrimary = "#111827"
  readonly textSecondary = "#6B7280"
  readonly borderDefault = "#E5E7EB"
  readonly accentPrimary = "#3B82F6"
  readonly textOnPrimary = "#FFFFFF"
  readonly actionBarBg = "#FFFFFF"
  readonly accentPrimaryActive = "#5E94FF"
  readonly accentFav = "#FFD700"
  readonly neutralLine = "#E5E7EB"
  readonly shadowSm = "rgba(0, 0, 0, 0.15)"
  readonly shadowLg = "rgba(0, 0, 0, 0.25)"
  readonly iconFilterMono = "none"

  readonly iconOverlayColor = "#E0E0E0"
  readonly iconOverlayOpacity = 0

  readonly button = {
    primaryBg: "#5B8CFF",
    primaryHover: "#4876E5",
    primaryActive: "#365FB0",
    primaryShadowHover: "rgba(0, 0, 0, 0.15)",
    textOnPrimary: "#FFFFFF",
    secondaryBg: "#FFFFFF",
    textOnSecondary: "#111827",
    disabledBg: "#F3F4F6",
    disabledText: "#9CA3AF",
    disabledBorder: "#E5E7EB",
    successBg: "#10B981",
    successText: "#FFFFFF",
    successHover: "#059669",
    successPressed: "#065F46",
  }

  readonly folder = {
    filledBg: "#3B82F6",
    textPrimary: "#111827",
    textSub: "#6B7280",
  }

  readonly destructive = {
    text: "#DC2626",
    textHighlight: "#FF7682",
    hover: "#FEF2F2",
    active: "#FEE2E2",
    card: "#FFFFFF",
  }

  readonly presence = {
    active: "#00C853",
    away: "#FFD700",
  }

  readonly codeEditor = {
    bg: "#CACACA",
    gutterBg: "#DBDBDB",
    lineNumber: "#555555",
    activeLine: "#FFFFFF",
    selection: "#ADD6FF",
  }

  readonly tooltip = {
    bgMain: "#333333",
    textLabel: "#FFFFFF",
    bgShortcut: "#4D4D4D",
    textShortcut: "#D1D5DB",
    bgCode: "#F8FAFC",
    textGutter: "#94A3B8",
    borderCode: "#E2E8F0",
    codeCmd: "#3B82F6",
    codeBraces: "#0F172A",
    codeParam: "#111827",
    codeKeyword: "#0550AE",
    codeString: "#24292F",
    codeParameter: "#953800",
    codeArgument: "#116329",
    codeComment: "#6E7781",
    codeMath: "#8250DF",
  }

  readonly input = {
    bg: "#FFFFFF",
    border: "#E2E8F0",
    borderFocus: "#5B8CFF",
    borderError: "#EF4444",
    error: "#EF4444",
    errorBg: "#FEF2F2",
    errorIcon: "#EF4444",
    successText: "#065F46",
    success: "#10B981",
    successBg: "#F0FDF4",
    warning: "#D97706",
    warningText: "#FFFBEB",
    warningBg: "#92400E",
    textTyped: "#0F172A",
    textPlaceholder: "#94A3B8",
    iconResting: "#64748B",
    iconActive: "#5B8CFF",
  }

  readonly social = {
    bg: "#FFFFFF",
    border: "#E2E8F0",
    hover: "#F8FAFC",
    dividerSubtle: "#E2E8F0",
    icon: "#1C1F26",
  }

  readonly nav = {
    bgActive: "#E0E9FF",
    textActive: "#5B8CFF",
    textRest: "#64748B",
    hover: "#E2E8F0",
  }

  readonly userCursors = [
    "#0066FF", "#33CC33", "#FF8C00", "#FF00FF", "#FFD700",
    "#228B22", "#8A2BE2", "#FF7F50", "#00BFFF", "#00FA9A",
    "#DC143C", "#008080", "#9370DB", "#FFBF00", "#20B2AA",
    "#EE82EE", "#B22222", "#00FFFF", "#FFB347", "#9932CC",
  ]

  readonly editorPanels = {
    header: {
      border: "#B9B9B9",
      backBg: "#F4F5F7",
      backBorder: "transparent",
      backText: "#1F2937",
      backHover: "#EBECEF",
      compileBg: "#FFFFFF",
      compileHover: "#F8FAFF",
      compileActive: "#F0F5FF",
      pathText: "#374151",
      titleText: "#111827",
    },
    icon: {
      filter: "none",
    },
    ribbon: {
      bg: "#FFFFFF",
      border: "#A6A6A6",
      text: "#4B5563",
      divider: "#D1D5DB",
      tabHover: "#E8E8E8",
      tabActiveBg: "#FFFFFF",
      tabActiveText: "#1F2937",
      fieldBorder: "#C5C5C5",
      fieldBg: "#FFFFFF",
      fieldHover: "#F8F8F8",
      fieldText: "#374151",
      iconHover: "#F3F4F6",
      groupActiveBg: "#FFFFFF",
      popupBg: "#FFFFFF",
      groupBg: "#F3F4F6",
      groupDivider: "#E5E7EB",
    },
    explorer: {
      canvasBg: "#F4F5F7",
      sidebarBorder: "#C7CDDB",
      panelBorder: "#C7CDDB",
      panelShadow: "0 1px 2px 0 rgb(0 0 0 / 0.05)",
      iconHoverBg: "#F9FAFB",
      selectedBg: "#DBEAFE",
      layersIconBg: "#FFFFFF",
      layersIconBorder: "1px solid #D1D5DB",
      layoutCanvasBg: "#F4F5F7",
    },
    explorerFrame: {
      background: "#FFFFFF",
      border: "#E5E7EB",
      hoverBg: "#F3F4F6",
      selectedBg: "#DCEAFF",
      textPrimary: "#111827",
      textMuted: "#6B7280",
      accent: "#5B8CFF",
      shadow: "#000000",
      danger: "#EF4444",
      folderIcon: "#3B82F6",
      fileIcon: "#3B82F6",
      chevronIcon: "#3B82F6",
      treeLine: "#555555",
    },
    dock: {
      btnBorder: "#D1D5DB",
    },
    codeEditorPanel: {
      breadcrumbBg: "#FFFFFF",
      breadcrumbBorder: "#E5E7EB",
      breadcrumbText: "#374151",
      breadcrumbDivider: "#D1D5DB",
      breadcrumbMutedText: "#9CA3AF",
    },
    console: {
      innerBg: "#F9FAFB",
      tabActiveBorder: "#D1D5DB",
      tabActiveBorderWidth: "1px",
      rowText: "#374151",
      rowBorder: "#E5E7EB",
      tabActiveText: "#111827",
      tabText: "#4B5563",
      tabHoverText: "#1F2937",
      emptyText: "#6B7280",
      codeBg: "rgba(0,0,0,0.03)",
      tabActiveBg: "#FFFFFF",
      warnRowBg: "#FEFCE8",
    },
    status: {
      errorBg: "#FEF2F2",
      errorBorder: "#FECACA",
      errorText: "#B91C1C",
      errorBadgeBg: "#FEE2E2",
      errorBadgeText: "#B91C1C",
      warnBg: "#FEFCE8",
      warnBorder: "#FEF08A",
      warnText: "#A16207",
      warnBadgeBg: "#FEF9C3",
      warnBadgeText: "#A16207",
      successBg: "#F0FDF4",
      successBorder: "#BBF7D0",
      successText: "#15803D",
      successBadgeBg: "#DCFCE7",
      successBadgeText: "#15803D",
      neutralBadgeBg: "#E5E7EB",
      linkText: "#2563EB",
      linkHover: "#1E40AF",
      errorTextStrong: "#DC2626",
      errorDismissHover: "#DC2626",
    },
    preview: {
      canvasBg: "#EDEDED",
      wrapperBg: "#FFFFFF",
      bgAlt: "#EDEDED",
      tabBarBg: "#F9FAFB",
      tabBorder: "#E5E7EB",
      contentBg: "#FFFFFF",
      text: "#374151",
      mutedText: "#4B5563",
      hintIcon: "#9CA3AF",
      errorText: "#EF4444",
      pageAreaBg: "#F9FAFB",
      imgBg: "#F9FAFB",
      imgBorder: "#E5E7EB",
    },
    pdf: {
      errorBg: "#FEF2F2",
      errorBorder: "#FECACA",
      errorText: "#B91C1C",
    },
    toggle: {
      onBg: "#FFFFFF",
      trackBg: "#E4E4E4",
      trackBorder: "rgba(200, 200, 200, 0.9)",
      knobBg: "#FAFAFA",
      knobBorderOn: "#5B8CFF",
      knobBorderOff: "#C5D4E8",
      labelOn: "#1A1A1A",
      labelOff: "#A8A8A8",
    },
    dropdown: {
      triggerBg: "#EFEFEF",
      triggerBorder: "#D0D0D0",
    },
    versionHistory: {
      headerBorder: "#E5E5E5",
      titleText: "#171717",
      closeText: "#525252",
      closeHoverBg: "#F5F5F5",
      subtext: "#525252",
      groupLabelBorder: "#F0F0F0",
      groupLabelText: "#666666",
      emptyText: "#737373",
      itemHoverBg: "#F5F5F5",
      itemSelectedBorder: "#E5E5E5",
      itemSelectedBg: "#F0F7FF",
      itemTimeText: "#888888",
      itemNameText: "#666666",
      itemDescText: "#333333",
      itemHashText: "#737373",
      iconAccent: "#2563EB",
      footerBorder: "#E5E5E5",
      footerBg: "#FAFAFA",
      footerLabel: "#737373",
      codeBorder: "#E5E5E5",
      codeBg: "#FFFFFF",
      codeText: "#262626",
      offlineBg: "#FFFBEB",
      offlineBorder: "#FDE68A",
      offlineText: "#78350F",
      scrollbarThumb: "#D1D5DB",
      panelShadow: "-2px 0 8px rgba(0,0,0,0.08)",
      loadingBorder: "#E5E5E5",
    },
    cookie: {
      border: "#D1D5DB",
      titleText: "#111827",
      bodyText: "#6B7280",
      btnBorder: "#D1D5DB",
      btnHover: "#F3F4F6",
    },
    tabs: {
      activeBg: "#FFFFFF",
      activeText: "#111827",
      inactiveText: "#4B5563",
      inactiveHoverText: "#111827",
      inactiveHoverBg: "#F3F4F6",
      barBg: "#F9FAFB",
      barBorder: "#E5E7EB",
      detachedBadgeBg: "#E5E7EB",
    },
    browserTab: {
      text: "#1E293B",
      btnBorder: "rgba(85, 85, 85, 0.4)",
      btnBorderWidth: "1px",
      btnBgPdf: "#FFFFFF",
      btnBgFile: "#F4F5F7",
    },
    divider: {
      bg: "transparent",
    },
  }

  readonly dashboardPanels = {
    topbar: {
      text: "#2563EB",
      notifText: "#374151",
      notifTextStrong: "#111827",
      notifBadgeBlue: "#EFF6FF",
      notifBadgeRed: "#FEF2F2",
      notifBadgeOrange: "#FFF7ED",
      notifBadgeGreen: "#F0FDF4",
    },
    surfaceHover: "rgb(91 140 255 / 0.2)",
    surfaceHoverMuted: "#F3F4F6",
    accentStrong: "#2563EB",
    accentHover: "#1D4ED8",
    strongText: "#000000",
    filenameText: "#1F2937",
    mutedTextAlt: "#9CA3AF",
    textPrimaryAlt: "#000000",
    textSecondary: "#6b7280",
    textMutedAlt: "#000000",
    cardSelectedBg: "#EFF6FF",
    chipBg: "#FFFFFF",
    chipText: "#374151",
    dialogText: "#374151",
    dialogTextFaint: "#D1D5DB",
    tagBg: "#F3F4F6",
    warnBg: "#FFFBEB",
    warnBorder: "#FDE68A",
    dangerBg: "#FEF2F2",
    dangerBorder: "#FECACA",
    iconMutedFilter: "brightness(0) saturate(0) opacity(0.65)",
    highlightBg: "#FEF08A",
    highlightText: "#1F2937",
  }
}

export const lightTheme = new LightTheme()
